import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { OrganizationService } from '@markant-platform/shared/organization/data-access';
import { AuthenticatedUserService } from '@markant-platform/shared/user/data-access';
import { catchError, EMPTY, map, Observable, of } from 'rxjs';
import { validateGLNCheckDigit } from '@markant-platform/shared/util-helpers';
import { HttpErrorResponse } from '@angular/common/http';
import { SnackbarService } from '@markant-platform/shared/snackbar/feature';
import { StaticRole } from '@markant-platform/shared/user/model';
import { Table, TableComponent } from '@markant-platform/shared/table/feature';
import { HeaderAndTableCellDraft, TableDraft, TableDraftBuilder } from '@markant-platform/shared/table/model';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ErrorMessage } from '@markant-platform/shared/error-model';
import { CommonModule } from '@angular/common';
import { MarkantElementsModule } from '@markant/elements-angular';
import { UiPageHeaderComponent } from '@markant-platform/shared/ui-components';
import { TranslocoPipe } from '@jsverse/transloco';
import { ModalBridgeComponent } from '@markant-platform/shared/ui-modal';
import { FormControlErrorPipe } from '@markant-platform/shared/util-pipes';
import { OrganizationDetailData } from 'data-model';

type OrganizationDetailDataExtended = {
  validatedGlnsParams: string[];
} & OrganizationDetailData;

@Component({
  selector: 'markant-platform-organization-glns-overview',
  templateUrl: 'organization-glns-overview.component.html',
  styleUrls: ['organization-glns-overview.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    MarkantElementsModule,
    UiPageHeaderComponent,
    TranslocoPipe,
    TableComponent,
    ModalBridgeComponent,
    FormControlErrorPipe,
    ReactiveFormsModule,
  ],
})
export class OrganizationGLNsOverviewComponent extends Table implements OnInit, OnDestroy {
  @Input({ required: true }) organization!: OrganizationDetailData;

  organizationId!: string | null;
  userRoles?: string[];
  openDeleteDialog = false;
  openAddModal = false;
  selectedGln = {
    value: '',
    organizationName: '',
  };

  inputForm: FormGroup<{ gln: FormControl<string | null> }> = this.createFormGroup();

  private readonly snackbarMessagesType = {
    delete: {
      success: 'organization.gln.snackbarActions.removeGln.success',
    },
    add: {
      success: 'organization.gln.snackbarActions.addGln.success',
    },
  };

  initialOrga!: OrganizationDetailData;

  deleteGln = (gln: string): void => {
    this.selectedGln.value = gln;
    this.selectedGln.organizationName = this.initialOrga.name;
    this.openDeleteDialog = true;
  };

  tableActions = [
    {
      icon: 'm_trash',
      colorScheme: 'dark',
      size: '11',
      function: this.deleteGln.bind(this) as (...args: unknown[]) => unknown,
    },
  ];

  TABLE_HEADER_AND_CELLS: HeaderAndTableCellDraft[] = [
    {
      headerCellColumnId: 'validatedGlnsParams',
      headerCellTranslationKey: 'organization.gln.gln',
      tableCellContentKey: 'validatedGlnsParams',
      tableCellContentType: 'STRING',
    },
    {
      headerCellColumnId: 'actions',
      headerCellTranslationKey: '',
      tableCellContentKey: 'validatedGlnsParams',
      actionElements: this.tableActions,
      tableCellContentType: 'ACTION',
    },
  ];

  override tableDraft: TableDraft = new TableDraftBuilder(this.TABLE_HEADER_AND_CELLS).disableRowLink(true).build();

  constructor(
    private readonly organizationService: OrganizationService,
    private readonly authenticatedUserService: AuthenticatedUserService,
    private readonly snackbarService: SnackbarService
  ) {
    super();
  }

  override ngOnInit(): void {
    this.organizationId = this.organization.id as string;

    this.getRequesterRoles()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(roles => {
        this.userRoles = roles;
        this.loadData();
      });
  }

  ngOnDestroy(): void {
    this.isLoading$.next(false);
  }

  loadData(): void {
    if (this.organizationId && this.userRoles?.includes(StaticRole.PLATFORM_ADMIN)) {
      this.organizationService
        .getOrganizationDetailsById(this.organizationId)
        .pipe(
          catchError(() => {
            this.snackbarService.showErrorSnackbarWithTransloco('organization.gln.errorLoadingData');
            return of({} as OrganizationDetailData);
          }),
          takeUntilDestroyed(this.destroyRef)
        )
        .subscribe(result => {
          this.reload(this.extendObject(result));
          this.initialOrga = result;
        });
    }
  }

  // extend organization object to fit table requirements
  private extendObject(organizationData: OrganizationDetailData): OrganizationDetailDataExtended[] {
    return (
      organizationData.validatedGlns?.map(gln => ({
        ...organizationData,
        validatedGlnsParams: [gln],
      })) ?? []
    );
  }

  closeDialog(): void {
    this.openAddModal = false;
    this.openDeleteDialog = false;
    this.resetInputForm();
  }

  addGln(): void {
    this.selectedGln.organizationName = this.initialOrga.name;
    this.openAddModal = true;
  }

  onSubmit(actionType: 'delete' | 'add'): void {
    const snackbarText = {
      success: '',
      error: '',
    };
    const glnToUpdate: string[] = this.initialOrga.validatedGlns ? [...this.initialOrga.validatedGlns] : [];

    if (!this.organizationId || !this.initialOrga) {
      return;
    }

    this.isLoading$.next(true);

    if (actionType === 'delete') {
      const indexToRemove = glnToUpdate.indexOf(this.selectedGln.value);
      glnToUpdate.splice(indexToRemove, 1);
      snackbarText.success = this.snackbarMessagesType.delete.success;
    } else {
      const glnToAdd = this.getGln();
      snackbarText.success = this.snackbarMessagesType.add.success;
      glnToUpdate.push(glnToAdd);
    }
    this.organizationService
      .setOrganizationDetailsById(this.organizationId, this.initialOrga, {
        ...this.initialOrga,
        validatedGlns: glnToUpdate,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.updatePageData();
          this.isLoading$.next(false);
          this.snackbarService.showSuccessSnackbarWithTransloco(snackbarText.success);
          return EMPTY;
        },
        error: (error: HttpErrorResponse) => {
          this.updatePageData();
          this.isLoading$.next(false);
          if (error.status !== 409 && error.error.message !== ErrorMessage.VERSION_MISMATCH) {
            this.snackbarService.showErrorSnackbarWithTransloco('organization.details.error.ORG-107');
          }
          return EMPTY;
        },
      });
  }

  private updatePageData(): void {
    this.closeDialog();
    this.loadData();
    this.resetInputForm();
  }

  getGln(): string {
    return this.inputForm.controls.gln.value || '';
  }

  getRequesterRoles(): Observable<string[]> {
    return this.authenticatedUserService.getUserRoles().pipe(map(roles => roles.map(({ name }) => name)));
  }

  resetInputForm(): void {
    this.inputForm.reset();
  }

  createFormGroup(): FormGroup<{ gln: FormControl<string | null> }> {
    return new FormGroup(
      {
        gln: new FormControl<string>('', [
          Validators.minLength(13),
          Validators.maxLength(13),
          Validators.pattern('^[0-9]*$'),
          Validators.required,
          validateGLNCheckDigit,
        ]),
      },
      { validators: [existingGln] }
    );
  }
}

export const existingGln: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  const gln = control.get('gln');
  const organizationData = control.get('organizationData');
  let existingGlnInOrganization = false;

  const organizationValidatedGlns = organizationData?.value?.validatedGlns;

  if (gln?.value && organizationValidatedGlns) {
    if (organizationValidatedGlns.indexOf(gln?.value) !== -1) {
      existingGlnInOrganization = true;
    }
  }

  return existingGlnInOrganization ? { duplicatedGln: true } : null;
};
